/**
 * @fileoverview OptimizationNotifications - Toast notifications for optimization events
 * Shows notifications when optimizations complete, fail, or encounter issues
 */

import React, { useEffect, useState, useRef } from 'react';
import { optimizationEventService } from '../../services/optimization/OptimizationEventService';
import type { OptimizationEvent } from '../../services/optimization/OptimizationEventService';

interface Notification {
    id: string;
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
    timestamp: Date;
    duration?: number;
}

interface OptimizationNotificationsProps {
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
    maxNotifications?: number;
    defaultDuration?: number;
}

export const OptimizationNotifications: React.FC<OptimizationNotificationsProps> = ({
    position = 'top-right',
    maxNotifications = 5,
    defaultDuration = 5000
}) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const timeoutRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());

    // Add notification
    const addNotification = (notification: Omit<Notification, 'id' | 'timestamp'>) => {
        const newNotification: Notification = {
            ...notification,
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            timestamp: new Date(),
            duration: notification.duration || defaultDuration
        };

        setNotifications(prev => {
            const updated = [newNotification, ...prev];
            // Keep only the latest maxNotifications
            return updated.slice(0, maxNotifications);
        });

        // Auto-remove after duration
        if (newNotification.duration && newNotification.duration > 0) {
            const timeoutId = setTimeout(() => {
                removeNotification(newNotification.id);
            }, newNotification.duration);

            // Store timeout reference for cleanup
            timeoutRefs.current.set(newNotification.id, timeoutId);
        }
    };

    // Remove notification
    const removeNotification = (id: string) => {
        setNotifications(prev => prev.filter(n => n.id !== id));

        // Clear the timeout if it exists
        const timeoutId = timeoutRefs.current.get(id);
        if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutRefs.current.delete(id);
        }
    };

    // Cleanup all timeouts on unmount
    useEffect(() => {
        return () => {
            timeoutRefs.current.forEach(timeoutId => clearTimeout(timeoutId));
            timeoutRefs.current.clear();
        };
    }, []);

    // Handle optimization events
    useEffect(() => {
        const handleOptimizationEvent = (event: OptimizationEvent) => {
            const strategy = 'parameters' in event.data ? 'Unknown' : 'Unknown';

            switch (event.type) {
                case 'completed':
                    addNotification({
                        type: 'success',
                        title: 'Optimization Complete',
                        message: `${strategy} optimization finished successfully`,
                        duration: 8000 // Longer for success
                    });
                    break;

                case 'failed':
                    const errorMsg = 'error' in event.data ? event.data.error : 'Unknown error';
                    addNotification({
                        type: 'error',
                        title: 'Optimization Failed',
                        message: `${strategy} optimization failed: ${errorMsg}`,
                        duration: 10000 // Longer for errors
                    });
                    break;

                case 'cancelled':
                    addNotification({
                        type: 'warning',
                        title: 'Optimization Cancelled',
                        message: `${strategy} optimization was cancelled`,
                        duration: 5000
                    });
                    break;

                case 'queued':
                    addNotification({
                        type: 'info',
                        title: 'Optimization Queued',
                        message: `${strategy} optimization added to queue`,
                        duration: 3000
                    });
                    break;
            }
        };

        // Subscribe to events for any job (global listener)
        const unsubscribe = optimizationEventService.subscribe('*', handleOptimizationEvent);

        return unsubscribe;
    }, []);

    // Position classes
    const positionClasses = {
        'top-right': 'top-4 right-4',
        'top-left': 'top-4 left-4',
        'bottom-right': 'bottom-4 right-4',
        'bottom-left': 'bottom-4 left-4'
    };

    // Get icon for notification type
    const getIcon = (type: Notification['type']) => {
        switch (type) {
            case 'success':
                return (
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                );
            case 'error':
                return (
                    <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                );
            case 'warning':
                return (
                    <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                );
            case 'info':
                return (
                    <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                );
        }
    };

    if (notifications.length === 0) return null;

    return (
        <div className={`fixed z-50 ${positionClasses[position]} space-y-2`}>
            {notifications.map((notification) => (
                <div
                    key={notification.id}
                    className="max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden animate-slide-in"
                >
                    <div className="p-4">
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                {getIcon(notification.type)}
                            </div>
                            <div className="ml-3 w-0 flex-1 pt-0.5">
                                <p className="text-sm font-medium text-gray-900">
                                    {notification.title}
                                </p>
                                <p className="mt-1 text-sm text-gray-500">
                                    {notification.message}
                                </p>
                            </div>
                            <div className="ml-4 flex-shrink-0 flex">
                                <button
                                    className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    onClick={() => removeNotification(notification.id)}
                                >
                                    <span className="sr-only">Close</span>
                                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ))}

            {/* CSS for animations */}
            <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
        </div>
    );
};